import { AttendanceGuage, Button } from '@wot/components';
import { ISession } from '@wot/typings';
import { addDays, compareAsc, endOfMonth, endOfWeek, parseISO, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import { format } from 'date-fns/esm';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CLOSE_ICON, GREEN_ARROW_UP, RED_ARROW_DOWN } from '../../assets/icons';
import { PROFILE_PIC_PLACEHOLDER } from '../../assets/images';
import BottomSheet from '../../components/Bottomsheet';
import Calender from '../../components/SetCalender';
import Slider from '../../components/Slider';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { getTutorRequest } from '../../reducers/profileSetupSlice';
import xhr from '../../utils/fetch';
import { currencyFormatter, getAttendacePercentage } from '../../utils/functions';
import { ClassItem } from '../MyCalender';
import { increasedPrecentage } from '../MyEarnings';
import { AcceptLicenseAgreement } from './Agreement';
import HorizontalStepper from './HorizontalStepper';

function NewUser() {
  const dispatch = useAppDispatch();
  const [agreementVisibility, setAgreementVisibility] = useState(false);
  const [calenderVisibility, setCalenderVisibility] = useState(false);

  const { request } = useAppSelector((state) => state.profileSetupSlice);
  const { profile } = useAppSelector((state) => state.profileSlice);
  const [profileProgress, setProfileProgress] = useState(0);
  const [upcomingSessions, setUpcomingSessions] = useState<ISession[]>([]);
  const [totalSessions, setTotalSessions] = useState<ISession[]>([]);
  const [range] = useState('This Week');
  const [actualEarnings, setActualEarnings] = useState(0);
  const [lastMonthActualEarnings, setLastMonthActualEarnings] = useState(0);
  const [currentMonthDeductions, setCurrentMonthDeductions] = useState(0);
  const [lastMonthDeductions, setLastMonthDeductions] = useState(0);
  const [currentPlansCount, setCurrentPlansCount] = useState(0);
  const [, setLastMonthPlansCount] = useState(0);

  const [studentsCount, setStudentsCount] = useState({
    current: 0,
    lastMonth: 0
  });

  const [demoClasses, setDemoClasses] = useState({
    current: 0,
    lastMonth: 0
  });

  const [currentMonth] = useState(new Date());
  const [chartData, setChartData] = useState<{ label: string; Earnings: number; Expenses: number }[]>([]);

  useEffect(() => {
    if (request?.status === 'rev-agreement') {
      setAgreementVisibility(true);
    } else {
      setAgreementVisibility(false);
    }
  }, [request]);

  useEffect(() => {
    if (!request && !profile) {
      return;
    }
    if (profile?.calender) {
      setProfileProgress(100);
      return;
    }
    if (request?.personal && request.education && request.experience && request.profession) {
      setProfileProgress(80);
      return;
    }
    if (request?.personal && request.education && request.experience) {
      setProfileProgress(60);
      return;
    }
    if (request?.personal && request.education) {
      setProfileProgress(40);
      return;
    }
    if (request?.personal) {
      setProfileProgress(20);
      return;
    }
    // setProfileProgress(0)
  }, [request, profile]);

  // Upcoming Sessions
  useEffect(() => {
    if (profile?._id) {
      xhr({
        url: `/t/${profile?._id}/classes`,
        method: 'GET'
      }).then((res) => {
        if (res.status === 'OK') {
          let sortedSlots = res.data.sort((a: any, b: any) =>
            compareAsc(new Date(a.scheduledTime.start), new Date(b.scheduledTime.start))
          );
          setUpcomingSessions(sortedSlots.filter((item: any) => item.status === 'upcoming'));
        }
      });
    }
  }, [profile]);

  // Total Sessions
  useEffect(() => {
    if (profile?._id) {
      xhr({
        url: `/t/${profile?._id}/sessions`,
        method: 'GET',
        params: {
          type: 'plan',
          status: 'completed'
        }
      })
        .then((res) => {
          if (res.status === 'OK') {
            setTotalSessions(res.data);
          }
        })
        .catch(() => { });
    }
  }, [profile]);

  // Actual Earnings And Last month earnings
  useEffect(() => {
    getEarnings(startOfMonth(currentMonth).toString(), endOfMonth(currentMonth).toString()).then((data) => {
      if (data) {
        setActualEarnings(data.reduce((acc: any, curr: any) => (acc += curr.total), 0));
      }
    });
    getEarnings(
      startOfMonth(subMonths(currentMonth, 1)).toString(),
      endOfMonth(subMonths(currentMonth, 1)).toString()
    ).then((data) => {
      if (data) {
        setLastMonthActualEarnings(data.reduce((acc: any, curr: any) => (acc += curr.total), 0));
      }
    });
    getDeductions(startOfMonth(currentMonth).toString(), endOfMonth(currentMonth).toString()).then((data) => {
      if (data) {
        setCurrentMonthDeductions(data.reduce((acc: any, curr: any) => (acc += curr.total), 0));
      }
    });
    getDeductions(
      startOfMonth(subMonths(currentMonth, 1)).toString(),
      endOfMonth(subMonths(currentMonth, 1)).toString()
    ).then((data) => {
      if (data) {
        setLastMonthDeductions(data.reduce((acc: any, curr: any) => (acc += curr.total), 0));
      }
    });
  }, []);

  const getEarnings = async (start: string, end: string) => {
    return await xhr({
      url: `/t/${profile?._id}/earnings?startAfter=${start}&endBefore=${end}`,
      method: 'GET'
    })
      .then((res) => {
        if (res.status === 'OK') {
          return res.data;
        } else {
          return null;
        }
      })
      .catch((err) => err);
  };

  const getDeductions = async (start: string, end: string) => {
    return await xhr({
      url: `/t/${profile?._id}/deductions?startAfter=${start}&endBefore=${end}`,
      method: 'GET'
    })
      .then((res) => {
        if (res.status === 'OK') {
          return res.data;
        } else {
          return null;
        }
      })
      .catch((err) => err);
  };

  const getPlansCount = async (start: string, end: string) => {
    if (profile?._id) {
      return await xhr({
        url: `/t/${profile?._id}/stats/plans-count?startAfter=${start}&endBefore=${end}`,
        method: 'GET'
      })
        .then((res) => {
          if (res.status === 'OK') {
            return res.data;
          } else {
            return null;
          }
        })
        .catch((err) => err);
    }
  };

  useEffect(() => {
    if (range === 'This Week') {
      let start = startOfWeek(new Date()).toString();
      let end = endOfWeek(new Date()).toString();
      getEarnings(start, end).then((res) => {
        let temp: any = [];
        let weekDay = startOfWeek(new Date());
        [...new Array(7)].map((_, i) => {
          let day = format(addDays(weekDay, i), 'EEE');
          let date = format(addDays(weekDay, i), 'd');
          const v = res.find((item: any) => format(parseISO(item.date), 'd') === date);
          temp.push({
            label: day,
            Earnings: (v && v.total) || 0,
            Expenses: 0
          });
        });
        setChartData(temp);
      });
    }
  }, []);

  useEffect(() => {
    getDemoClasses().then((data) => data && setDemoClasses({ current: data.length, lastMonth: 0 }));
    getStudentsCount(startOfMonth(currentMonth).toString(), endOfMonth(currentMonth).toString()).then(
      (data) => data && setStudentsCount({ current: data.count, lastMonth: data.previousCount })
    );
    getPlansCount(startOfMonth(currentMonth).toString(), endOfMonth(currentMonth).toString()).then((data) => {
      if (data) {
        setCurrentPlansCount(data.count);
      }
    });
    getPlansCount(
      startOfMonth(subMonths(currentMonth, 1)).toString(),
      endOfMonth(subMonths(currentMonth, 1)).toString()
    ).then((data) => {
      if (data) {
        setLastMonthPlansCount(data.count);
      }
    });
  }, [profile]);

  const getDemoClasses = async () => {
    if (profile?._id) {
      return await xhr({
        url: `/t/${profile?._id}/sessions`,
        method: 'GET',
        params: {
          type: 'trial'
        }
      })
        .then((res) => {
          if (res.status === 'OK') {
            return res.data;
          }
        })
        .catch(() => { });
    }
  };

  const getStudentsCount = async (start: string, end: string) => {
    if (profile?._id) {
      return await xhr({
        url: `/t/${profile?._id}/stats/students-count/?startAfter=${start}&endBefore=${end}`,
        method: 'GET'
      })
        .then((res) => {
          if (res.status === 'OK') {
            return res.data;
          }
        })
        .catch(() => {
          return;
        });
    }
  };

  return (
    <div className="w-full min-h-screen  container mx-auto">
      {agreementVisibility && <AcceptLicenseAgreement setAgreementVisibility={setAgreementVisibility} />}
      {calenderVisibility && (
        <BottomSheet>
          <div className="w-full h-full relative mx-auto bg-white flex flex-col items-center justify-start p-5 border-b overflow-y-scroll">
            <h1 className="text-2xl font-semibold">Set Availability</h1>
            <div onClick={() => setCalenderVisibility(false)} className="absolute right-4 top-4">
              <img src={CLOSE_ICON} className="cursor-pointer" />
            </div>
            <Calender setCalenderVisibility={setCalenderVisibility} />
          </div>
        </BottomSheet>
      )}
      <div>
        <div className="w-full h-full py-10 px-4 md:p-10">
          <div className="w-full h-full grid lg:grid-rows-2 gap-8">
            {/* <div className="flex flex-col md:flex-row md:flex-auto gap-8"> */}
            <div className="lg:row-span-2 grid lg:grid-cols-7 gap-8">
              <div className="px-6 py-4 w-full min-w-[256px] border flex flex-col justify-center border-gray-300 rounded-xl bg-white lg:col-span-2 items-center">
                <img
                  src={
                    request?.personal?.data.profilePic || profile?.personal.data.profilePic || PROFILE_PIC_PLACEHOLDER
                  }
                  alt="Profile Picture"
                  className="h-20 w-20 object-cover rounded-full"
                />
                <h2 className="text-lg font-medium p-2">
                  {request?.personal?.data.name || profile?.personal.data.name}
                </h2>
                <div className="flex justify-center divide-x">
                  <div className="flex flex-col items-center px-1">
                    <span className="text-sm font-medium">{studentsCount.current || 0}</span>
                    <span className="text-xs font-medium text-neutralGray2">Students</span>
                  </div>
                  <div className="flex flex-col items-center px-1">
                    <span className="text-sm font-medium">
                      {getAttendacePercentage(
                        totalSessions.filter((i) => i.attendance && i.attendance.student === 'present').length,
                        totalSessions.length
                      )}%
                    </span>
                    <span className="text-xs font-medium text-neutralGray2">Attendance</span>
                  </div>
                  {/* <div className="flex flex-col items-center px-1">
                    <span className="text-sm font-medium"></span>
                    <span className="text-xs font-medium text-neutralGray2">Online Exp.</span>
                  </div>
                  <div className="flex flex-col items-center px-1">
                    <span className="text-sm font-medium">0</span>
                    <span className="text-xs font-medium text-neutralGray2">Offline Exp.</span>
                  </div> */}
                </div>
                <div className="flex justify-center flex-wrap py-2">
                  {profile?.profession.data.categories.map((item: any, i: any) => (
                    <span
                      className="rounded-full border bg-neutralBlue2 text-xs font-medium px-3 py-1 mx-1 mt-2 group-hover:bg-white"
                      key={i}
                    >
                      {item.name}
                    </span>
                  ))}
                </div>
                {!profile?._id && (
                  <>
                    <div className="w-full">
                      <div className="flex flex-row items-center justify-between pb-2 pt-8">
                        <p>Profile Completion</p>
                        <p>{profileProgress}%</p>
                      </div>
                      <div className="relative rounded-full w-full bg-neutralBlue1 h-2">
                        <div
                          style={{ width: `${profileProgress}%` }}
                          className={`absolute rounded-full bg-primary h-2`}
                        ></div>
                      </div>
                    </div>
                    {profileProgress < 80 && (
                      <div className="w-40 pt-20 pb-10">
                        <Link to={'/profilesetup'}>
                          <Button text="Complete Profile" onClick={() => { }} />
                        </Link>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="px-6 py-4 w-full min-w-[256px] border border-gray-300 rounded-xl items-center bg-neutralBlue2 lg:col-span-5 shadow-lg">
                {(request?.status === 'pending' && !profile) || (!request && !profile) && (
                  <div className="sm:px-6">
                    <h2 className="text-2xl font-medium pt-6">Start your journey now</h2>
                    <p className="text-md font-medium text-neutralGray3 pt-2 pb-6">
                      Complete your request verification to start taking classes
                    </p>
                    <HorizontalStepper />
                    <div className="flex flex-row justify-end">
                      <div className="w-40 py-8">
                        <Link to={'/profilesetup'}>
                          <Button text="Complete Profile" onClick={() => { }} />
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
                {request?.status === 'under-review' && (
                  <div className="flex flex-col gap-4">
                    <h2 className="text-2xl font-medium pt-6">Your Profile is Under Review</h2>
                    <p className="text-sm">We will verify Your request and get back to you soon.</p>
                  </div>
                )}
                {request?.status === 'revision' && (
                  <div className="w-full h-full flex flex-col justify-between">
                    <div className="flex flex-col  gap-4">
                      <h2 className="text-2xl font-medium pt-6">You have received a revision request from the admin</h2>
                      <p className="text-sm">
                        Some of the details are not matching with your ID. View revisions requested from the admin under
                        personal details and education and re-submit it for verification.
                      </p>
                    </div>
                    <div className="w-full flex flex-row justify-end">
                      <div className="w-40 py-8 ">
                        <Link to={'/profilesetup'}>
                          <Button text="View Revisions" onClick={() => { }} />
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
                {request?.status === 'gen-agreement' && (
                  <div className="w-full h-full flex justify-between">
                    <div className="flex flex-col  gap-4">
                      <h2 className="text-2xl font-medium pt-6">
                        Congratulations, your request has been successfully verified.{' '}
                      </h2>
                      <p className="text-sm">You will recieve an agreement soon.</p>
                    </div>
                    <Button
                      text='Refresh'
                      onClick={() => {
                        if (request) {
                          dispatch(getTutorRequest(request._id));
                        }
                      }}
                      type='s-outlined'
                    />
                  </div>
                )}
                {request?.status === 'rev-agreement' && (
                  <div className="w-full h-full flex flex-col justify-between">
                    {request.agreement && (
                      <>
                        <div className="flex flex-col  gap-4">
                          <h2 className="text-2xl font-medium pt-6">
                            Congratulations, your request has been successfully verified.{' '}
                          </h2>
                          <p className="text-sm">
                            As a last step, Please go through the tutor agreement and accept the terms and policies.
                            After that you will be ready to get hired by students.
                          </p>
                        </div>
                        <div className="w-full flex flex-row justify-end">
                          <div className="w-40 py-8 ">
                            <Button text="Open Agreement" onClick={() => setAgreementVisibility(true)} />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {!request && profile && (
                  <>
                    {profile?.status === 'in-active' && !profile?.calender ? (
                      <div className="w-full h-full flex flex-col justify-between">
                        <div className="flex flex-col  gap-4">
                          <h2 className="text-lg md:text-2xl font-medium pt-6">
                            Congratulations, you are now verified as a tutor!
                          </h2>
                          <p className="text-sm">
                            You can now set your availability on the calendar so that students can view your slots and
                            make a booking with you
                          </p>
                        </div>
                        <div className="w-full flex flex-row justify-end">
                          <div className="w-40 py-8 ">
                            <Button text="Set availability" onClick={() => setCalenderVisibility(true)} />
                          </div>
                        </div>
                      </div>
                    ) : profile?.status === 'active' ? (
                      <h2 className="text-xl md:text-2xl font-medium pt-6">No Upcoming Classes!</h2>
                    ) : (
                      <h2 className="text-xl md:text-2xl font-medium pt-6">Your Profile is in-active!</h2>
                    )}
                  </>
                )}
              </div>
            </div>

            {!request && profile && (
              <>
                <div className="hidden lg:row-span-3 lg:grid lg:grid-cols-7  gap-8">
                  <div className="lg:col-span-2 lg:row-span-2 h-full">
                    <CardContainer
                      title="Upcoming Sessions"
                      link="/calendar"
                      content={
                        upcomingSessions.length > 0 ? (
                          <div className="flex flex-col gap-4">
                            {upcomingSessions.slice(0, 2).map((session) => (
                              <ClassItem key={session._id} session={session} />
                            ))}
                          </div>
                        ) : null
                      }
                      placeHolder={
                        <div className="flex flex-col gap-5 items-center">
                          <p className="text-sm">No Classes found.</p>
                        </div>
                      }
                    />
                  </div>
                  <div className="lg:col-span-5">
                    <CardContainer
                      title="Earnings"
                      content={
                        <div className="w-full flex gap-4 items-stretch">
                          <div className="grow flex flex-col p-8 gap-5 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                            <div className="flex justify-between gap-2">
                              <h1 className="text-2xl font-bold">
                                INR {chartData.reduce((acc, currValue) => acc + currValue.Earnings, 0).toFixed(2)}
                              </h1>
                              <h1 className="text-base font-normal">This Week</h1>
                            </div>

                            <div className="w-full h-[164px] mt-4">
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  width={730}
                                  height={164}
                                  data={chartData}
                                  margin={{
                                    top: 5,
                                    right: 30,
                                    left: 20,
                                    bottom: 5
                                  }}
                                >
                                  <CartesianGrid vertical={false} strokeDasharray="1" />
                                  <XAxis dataKey="label" fontSize={12} />
                                  <YAxis axisLine={false} width={20} fontSize={12} orientation="right" />
                                  <Tooltip />
                                  <Bar dataKey="Earnings" fill="#8884d8" barSize={10}>
                                    {chartData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill="#EA4459" strokeLinecap="round" />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                          {/* <div className='w-full flex flex-wrap gap-4'> */}
                          <div className="w-[200px] flex flex-col gap-7 p-8 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                            <h3 className="text-2xl font-semibold">Earnings this month</h3>
                            <h1 className="text-2xl">INR {currencyFormatter(actualEarnings)}</h1>
                            <div className="flex items-center gap-2">
                              {lastMonthActualEarnings > actualEarnings ? (
                                <>
                                  <p className="text-red-500 text-sm">
                                    {increasedPrecentage(lastMonthActualEarnings, actualEarnings)}%
                                  </p>
                                  <img src={RED_ARROW_DOWN} className="w-6 h-6" />
                                </>
                              ) : (
                                <>
                                  <p className="text-green-500 text-sm">
                                    {increasedPrecentage(lastMonthActualEarnings, actualEarnings)}%
                                  </p>
                                  <img src={GREEN_ARROW_UP} className="w-6 h-6" />
                                </>
                              )}
                            </div>
                          </div>

                          <div className="w-[200px] flex flex-col gap-7 p-8 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                            <h3 className="text-2xl font-semibold">Deductions this month</h3>
                            <h1 className="text-2xl">INR {currencyFormatter(currentMonthDeductions)}</h1>
                            <div className="flex items-center gap-2">
                              {lastMonthDeductions > currentMonthDeductions ? (
                                <>
                                  <p className="text-green-500 text-sm">
                                    {increasedPrecentage(lastMonthDeductions, currentMonthDeductions)}%
                                  </p>
                                  <img src={GREEN_ARROW_UP} className="w-6 h-6" />
                                </>
                              ) : (
                                <>
                                  <p className="text-red-500 text-sm">
                                    {increasedPrecentage(lastMonthDeductions, currentMonthDeductions)}%
                                  </p>
                                  <img src={RED_ARROW_DOWN} className="w-6 h-6" />
                                </>
                              )}
                            </div>
                          </div>
                          {/* </div> */}
                        </div>
                      }
                      placeHolder={
                        <div className="flex flex-col gap-5 items-center">
                          <p className="text-sm">Your data related will show up here once you start taking classes.</p>
                        </div>
                      }
                    />
                  </div>
                  <div className="lg:col-span-3">
                    <CardContainer
                      title="Analytics"
                      content={
                        <div className="w-full flex gap-4 justify-between">
                          <div className="shrink-0 grow basis-1 flex flex-col gap-7 p-4 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                            <h3 className="text-xl font-semibold">Bookings</h3>
                            <div className="flex justify-between">
                              <h1 className="text-2xl">{currentPlansCount || 0}</h1>
                              {/* <div className="flex items-center gap-2">
                            {lastMonthDeductions > currentMonthDeductions ? (
                              <>
                                <p className="text-red-500 text-sm">
                                  {increasedPrecentage(lastMonthDeductions, currentMonthDeductions)}%
                                </p>
                                <img src={RED_ARROW_DOWN} className="w-6 h-6" />
                              </>
                            ) : (
                              <>
                                <p className="text-green-500 text-sm">
                                  {increasedPrecentage(lastMonthDeductions, currentMonthDeductions)}%
                                </p>
                                <img src={GREEN_ARROW_UP} className="w-6 h-6" />
                              </>
                            )}
                          </div> */}
                            </div>
                          </div>
                          <div className="shrink-0 grow basis-1 flex flex-col gap-7 p-4 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                            <h3 className="text-xl font-semibold">Students</h3>
                            <div className="flex justify-between">
                              <h1 className="text-2xl">{studentsCount.current || 0}</h1>
                              {/* <div className="flex items-center gap-2">
                            {studentsCount.lastMonth > studentsCount.current ? (
                              <>
                                <p className="text-red-500 text-sm">
                                  {increasedPrecentage(studentsCount.lastMonth, studentsCount.current)}%
                                </p>
                                <img src={RED_ARROW_DOWN} className="w-6 h-6" />
                              </>
                            ) : (
                              <>
                                <p className="text-green-500 text-sm">
                                  {increasedPrecentage(studentsCount.lastMonth, studentsCount.current)}%
                                </p>
                                <img src={GREEN_ARROW_UP} className="w-6 h-6" />
                              </>
                            )}
                          </div> */}
                            </div>
                          </div>
                          <div className="shrink-0 grow basis-1 flex flex-col gap-7 p-4 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                            <h3 className="text-xl font-semibold">Demo Classes</h3>
                            <div className="flex justify-between">
                              <h1 className="text-2xl">{demoClasses.current}</h1>
                              {/* <div className="flex items-center gap-2">
                            {demoClasses.lastMonth > demoClasses.current ? (
                              <>
                                <p className="text-red-500 text-sm">
                                  {increasedPrecentage(demoClasses.lastMonth, demoClasses.current)}%
                                </p>
                                <img src={RED_ARROW_DOWN} className="w-6 h-6" />
                              </>
                            ) : (
                              <>
                                <p className="text-green-500 text-sm">
                                  {increasedPrecentage(demoClasses.lastMonth, demoClasses.current)}%
                                </p>
                                <img src={GREEN_ARROW_UP} className="w-6 h-6" />
                              </>
                            )}
                          </div> */}
                            </div>
                          </div>
                        </div>
                      }
                      placeHolder={
                        <div className="flex flex-col gap-5 items-center">
                          <p className="text-sm">
                            Your overall reports will show up here once a student books a plan with you
                          </p>
                        </div>
                      }
                    />
                  </div>
                  <CardContainer
                    title="My Attendance"
                    link="/attendance"
                    content={
                      <>
                        {totalSessions ? (
                          <div className="w-full  max-h-[256px] flex flex-col items-center bg-white rounded-xl p-4">
                            <div className="w-[40%] md:w-[50%] text-2xl grow flex items-center justify-center p-4">
                              <AttendanceGuage
                                percentage={getAttendacePercentage(
                                  totalSessions.filter((i) => i.attendance && i.attendance.student === 'present')
                                    .length,
                                  totalSessions.length
                                )}
                              />
                            </div>
                          </div>
                        ) : null}
                      </>
                    }
                    placeHolder={
                      <p className="font-medium">
                        YYour attendance stats will show up here once you start taking classes
                      </p>
                    }
                  />
                </div>
                <div className="lg:hidden row-span-3 grid grid-cols-1 gap-8">
                  <Slider>
                    <CardContainer
                      title="Upcoming Sessions"
                      link="/calendar"
                      content={
                        upcomingSessions.length > 0 ? (
                          <div className="flex flex-col gap-4">
                            {upcomingSessions.slice(0, 1).map((session) => (
                              <ClassItem key={session._id} session={session} />
                            ))}
                          </div>
                        ) : null
                      }
                      placeHolder={
                        <div className="flex flex-col gap-5 items-center">
                          <p className="text-sm">No Classes found.</p>
                        </div>
                      }
                    />
                    <CardContainer
                      title="Earnings"
                      content={
                        <div className="flex gap-4 items-stretch">
                          <div className="flex flex-col gap-7 p-8 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                            <h3 className="text-2xl font-semibold">Earnings this month</h3>
                            <h1 className="text-2xl">INR {currencyFormatter(actualEarnings)}</h1>
                            <div className="flex items-center gap-2">
                              {lastMonthActualEarnings > actualEarnings ? (
                                <>
                                  <p className="text-red-500 text-sm">
                                    {increasedPrecentage(lastMonthActualEarnings, actualEarnings)}%
                                  </p>
                                  <img src={RED_ARROW_DOWN} className="w-6 h-6" />
                                </>
                              ) : (
                                <>
                                  <p className="text-green-500 text-sm">
                                    {increasedPrecentage(lastMonthActualEarnings, actualEarnings)}%
                                  </p>
                                  <img src={GREEN_ARROW_UP} className="w-6 h-6" />
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      }
                      placeHolder={
                        <div className="flex flex-col gap-5 items-center">
                          <p className="text-sm">Your data related will show up here once you start taking classes.</p>
                        </div>
                      }
                    />
                    <CardContainer
                      title="My Attendance"
                      link="/attendance"
                      content={
                        <>
                          {totalSessions ? (
                            <div className="w-full  max-h-[256px] flex flex-col items-center bg-white rounded-xl p-4">
                              <div className="w-[40%] md:w-[50%] text-2xl grow flex items-center justify-center p-4">
                                <AttendanceGuage
                                  percentage={getAttendacePercentage(
                                    totalSessions.filter((i) => i.attendance && i.attendance.student === 'present')
                                      .length,
                                    totalSessions.length
                                  )}
                                />
                              </div>
                            </div>
                          ) : null}
                        </>
                      }
                      placeHolder={
                        <p className="font-medium">
                          Your attendance stats will show up here once you are enrolled for any class
                        </p>
                      }
                    />
                  </Slider>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewUser;

interface Props {
  title: string;
  content?: any;
  link?: string;
  placeHolder?: any;
}

export function CardContainer({ title, content, link, placeHolder }: Props) {
  return (
    <div className="w-full h-full bg-white flex flex-col p-5 rounded-2xl gap-6 min-w-[320px] min-h-[256px] lg:drop-shadow-md">
      <div className="flex flex-row justify-between items-center">
        <p className="text-lg font-extrabold">{title}</p>
        {link && (
          <Link to={link}>
            <p className="text-sm font-medium text-black cursor-pointer hover:text-black/80">View All</p>
          </Link>
        )}
      </div>
      <div className="w-full h-full">{content ? content : placeHolder}</div>
    </div>
  );
}
